import { useContext } from 'react';
import { Box, Button, Icon, Paragraph } from '@zillow/constellation-10';
import { IconSyncFilled } from '@zillow/constellation-icons';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import { LeadManagementContext } from '../../../../contexts/LeadManagementContextProvider';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import useQueryParamsNext from '../../../../hooks/useQueryParamsNext';
import { switchConversationChipRowStyles, switchConversationChipStyles } from '../styles';

type SwitchConversationChipProps = {
  conversationId: string;
};

/**
 * One-click switch of the messaging window onto the lead whose details drawer is open, shown as a
 * chip floating over the top of the thread. Deliberately not automatic — an open chat may hold a
 * draft or be parked awaiting a reply.
 */
export default function SwitchConversationChip({ conversationId }: SwitchConversationChipProps) {
  const { conversationId: renterDetailsConversationId } = useContext(LeadManagementContext);
  const { updateQueryParams } = useQueryParamsNext();
  const drawerLead = useCachedLeadByConversationId(renterDetailsConversationId);

  if (!renterDetailsConversationId || renterDetailsConversationId === conversationId) {
    return null;
  }

  const drawerLeadName = drawerLead?.renterInfo?.renterName;

  const handleSwitchClick = () => {
    updateQueryParams({
      queryParamValues: { messagingConversationId: renterDetailsConversationId },
      shouldReplaceHistory: true,
    });
  };

  return (
    <Box css={switchConversationChipRowStyles}>
      <Button
        className={FULLSTORY_EXCLUDE_CLASS_NAME}
        emphasis="bare"
        css={switchConversationChipStyles}
        onClick={handleSwitchClick}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          <Icon render={<IconSyncFilled />} css={{ color: 'text.accent.blue.hero' }} />
          <Paragraph
            textStyle="body-sm-bold"
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 260px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            css={{ truncate: true, maxWidth: '260px', color: 'text.accent.blue.hero' }}
          >
            {drawerLeadName ? `Switch chat to ${drawerLeadName}` : 'Switch chat to this lead'}
          </Paragraph>
        </Box>
      </Button>
    </Box>
  );
}
